import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, Gift, Camera, Type } from "lucide-react";
import MiniGridLoader from '@/components/MiniGridLoader';
import { toast } from "sonner";
import { motion } from "framer-motion";
import creditsConfig from "@/config/credits.json";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useUserCredits } from "@/hooks/useUserCredits";
import { useProfile } from "@/hooks/useProfile";
import { isNativeApp } from "@/lib/platform";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { captureException, addBreadcrumb, setContext } from "@/lib/sentry";
import { 
  loginToRevenueCat, 
  purchasePackage,
  APPLE_PRICING_USD
} from "@/lib/purchases";
import { formatCurrency } from "@/lib/utils";

const CreditsSuccess = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [verifying, setVerifying] = useState(false);
  const [countdown, setCountdown] = useState(5);
  const { user } = useAuth();
  const { data: userCredits } = useUserCredits(user?.id);
  const queryClient = useQueryClient();



  const { data: profile } = useProfile(user?.id);

  // Redirect non-pro users to upgrade page
  useEffect(() => {
    if (profile && !profile.is_premium) {
      navigate("/upgrade", { replace: true });
    }
  }, [profile, navigate]);

  // Auto-redirect countdown for web (non-native) after successful payment
  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId) return; // Only run for success page, not buy credits page
    
    const isWeb = !isNativeApp();
    if (!isWeb) return; // Only auto-redirect on web
    
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate("/track", { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [searchParams, navigate]);

  useEffect(() => {
    const handleSuccess = async () => {
      const sessionId = searchParams.get("session_id");

      // If no session_id, user came here directly - show buy credits UI
      if (!sessionId) {
        return;
      }

      // Request server to verify payment with Stripe and award credits
      console.log('[CreditsSuccess] Requesting payment verification from server...');
      try {
        const { data, error } = await supabase.functions.invoke("verify-credit-payment", {
          body: { session_id: sessionId },
        });

        if (error) {
          console.error('[CreditsSuccess] Verification error:', error);
          toast.error(t('credits_success.payment_verify_error'));
        } else if (data?.success) {
          const creditsDisplay = data.credits_added / 10;
          if (data.already_processed) {
            console.log('[CreditsSuccess] Credits were already awarded:', creditsDisplay);
            toast.success(t('credits_success.credits_confirmed', { count: creditsDisplay }));
          } else {
            console.log('[CreditsSuccess] Credits awarded:', creditsDisplay);
            toast.success(t('credits_success.credits_added_toast', { count: creditsDisplay }));
          }
        } else {
          console.warn('[CreditsSuccess] Unexpected response:', data);
          toast.error(t('credits_success.payment_verify_failed'));
        }
      } catch (e) {
        console.error('[CreditsSuccess] Unexpected error:', e);
        captureException(e, {
          tags: { page: 'credits-success', action: 'stripe-verification' },
          extra: { sessionId: searchParams.get('session_id') },
        });
        toast.error(t('credits_success.payment_error'));
      }

      // Invalidate only credits-related queries, preserve profile data
      console.log('[CreditsSuccess] Invalidating credits cache (preserving profile)');
      queryClient.invalidateQueries({ queryKey: ["user-credits"] });
      queryClient.invalidateQueries({ queryKey: ["subscription-status"] });
    };

    handleSuccess();
  }, [searchParams, queryClient]);

  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="p-8 max-w-md w-full text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 animate-spin text-primary" />
          <h2 className="text-2xl font-bold mb-2">{t('credits_success.processing')}</h2>
          <p className="text-muted-foreground">{t('credits_success.please_wait')}</p>
        </Card>
      </div>
    );
  }

  // Show buy credits page if no session_id
  if (!searchParams.get("session_id")) {
    const handleBuyCredits = async () => {
      // Credits can only be purchased in mobile app.
      if (!isNativeApp()) {
        toast.error(t('credits_success.mobile_only_error'));
        return;
      }
      
      // On native devices, go directly to Apple purchase
      await handleAppleBuyCredits();
    };



    const handleAppleBuyCredits = async () => {
      if (!user) {
        toast.error(t('credits_success.user_not_authenticated'));
        return;
      }

      setVerifying(true);

      try {
        // Login to RevenueCat with security validation
        const loginResult = await loginToRevenueCat(user.id, user.email || "", supabase);
        if (!loginResult.success) {
          // Check if this is an account transfer attempt
          if (loginResult.error?.includes('another account')) {
            toast.error(t('credits_success.linked_another_account'), {
              description: t('credits_success.cannot_transfer')
            });
            return;
          }
          throw new Error(loginResult.error || "Failed to login to RevenueCat");
        }

        console.log('[CreditsSuccess] Logged into RevenueCat:', loginResult.customerId);

        // Purchase credits through Apple (consumable product)
        console.log('[CreditsSuccess] Attempting to purchase package: Credits');
        const purchaseResult = await purchasePackage("Credits");
        console.log('[CreditsSuccess] Purchase result:', JSON.stringify(purchaseResult));
        
        if (!purchaseResult.success) {
          // Show detailed error to help diagnose the issue
          const errorMsg = purchaseResult.error || "Purchase failed";
          console.error('[CreditsSuccess] Purchase failed:', errorMsg);
          throw new Error(`App Store Error: ${errorMsg}. This may be because the Credits product is not configured in App Store Connect or RevenueCat.`);
        }

        console.log('[CreditsSuccess] Apple credits purchase completed');

        // Wait longer for RevenueCat to sync the purchase from Apple
        const loadingToast = toast.loading(t('credits_success.verifying_purchase'));
        await new Promise(resolve => setTimeout(resolve, 5000)); // 5 seconds initial wait

        // Get current session for auth
        const { data: sessionData } = await supabase.auth.getSession();
        
        // Verify and award credits via backend with retry logic
        // Since transactionId may not be available for consumables, we'll verify by checking
        // for the most recent purchase from RevenueCat
        let verificationSuccess = false;
        let lastError = null;
        
        // Increased to 5 attempts with longer delays
        for (let attempt = 1; attempt <= 5; attempt++) {
          console.log(`[CreditsSuccess] Verification attempt ${attempt}/5`);
          
          const { data, error } = await supabase.functions.invoke("verify-revenuecat-purchase", {
            body: { 
              user_id: user.id,
              product_id: "GitFit.AI_Credits_Tier1" // App Store product ID (not RevenueCat reference ID)
              // Note: transaction_id not sent because SDK doesn't provide it for consumables
            },
            headers: {
              Authorization: `Bearer ${sessionData.session?.access_token}`,
            },
          });

          if (!error && data?.success) {
            verificationSuccess = true;
            toast.dismiss(loadingToast);
            const creditsDisplay = data.credits_added / 10;
            toast.success(t('credits_success.credits_added_toast', { count: creditsDisplay }));
            // Refresh credits balance (preserve profile data)
            console.log('[CreditsSuccess] Invalidating credits cache (preserving profile)');
            queryClient.invalidateQueries({ queryKey: ["user-credits"] });
            queryClient.invalidateQueries({ queryKey: ["subscription-status"] });
            // Navigate to track page after successful purchase
            navigate("/track", { replace: true });
            break;
          }

          lastError = error || data?.error || "Unknown error";
          console.log(`[CreditsSuccess] Attempt ${attempt} failed:`, lastError);
          
          // Wait before retrying with exponential backoff (3s, 6s, 9s, 12s)
          if (attempt < 5) {
            const waitTime = 3000 * attempt;
            console.log(`[CreditsSuccess] Waiting ${waitTime}ms before retry...`);
            await new Promise(resolve => setTimeout(resolve, waitTime));
          }
        }

        toast.dismiss(loadingToast);
        
        if (!verificationSuccess) {
          console.error('[CreditsSuccess] All verification attempts failed:', lastError);
          throw new Error(t('credits_success.purchase_verification_failed'));
        }
      } catch (error: any) {
        console.error('[CreditsSuccess] Apple purchase error:', error);
        captureException(error, {
          tags: { page: 'credits-success', action: 'apple-purchase' },
          extra: { 
            productId: 'credits_purchase',
            verificationAttempts: 3,
          },
        });
        toast.error(error.message || t('credits_success.apple_purchase_failed'));
      } finally {
        setVerifying(false);
      }
    };

    return (
      <motion.div 
        className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 flex items-center justify-center p-4 py-6 md:py-4"
        initial={{ y: "100%" }}
        animate={{ y: 0 }}
        transition={{ type: "spring", damping: 30, stiffness: 300 }}
      >
        <div className="max-w-md w-full space-y-4 md:space-y-6">
          {/* Header */}
          <div className="text-center space-y-2 md:space-y-3">
            <div className="inline-flex items-center justify-center w-12 h-12 md:w-16 md:h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-primary/10 mb-1 md:mb-2 shadow-sm">
              <Sparkles className="w-6 h-6 md:w-8 md:h-8 text-primary" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold">{t('credits_success.buy_credits')}</h1>
            <p className="text-sm md:text-base text-muted-foreground">
              {t('credits_success.top_up_subtitle')}
            </p>
          </div>

          {/* Main Card */}
          <Card className="p-4 md:p-6 shadow-lg border-primary/10">
            <div className="space-y-4 md:space-y-6">
              {/* Pricing */}
              <div className="text-center py-4 md:py-6 px-4 rounded-xl bg-gradient-to-br from-primary/5 to-primary/10">
                <div className="space-y-1 mb-2">
                  <div className="text-3xl md:text-4xl font-bold bg-gradient-to-br from-primary to-primary/60 bg-clip-text text-transparent">
                    {formatCurrency(APPLE_PRICING_USD.CREDITS)}
                  </div>
                  {!isNativeApp() && (
                    <div className="text-xs md:text-sm text-amber-600 font-medium">{t('credits_success.mobile_only_notice')}</div>
                  )}
                </div>
                <div className="text-base md:text-lg font-semibold text-foreground mb-1">
                  {creditsConfig.purchase.credits} AI Credits <span className="text-xs md:text-sm text-muted-foreground font-normal">~{creditsConfig.purchase.credits} meal scans</span>
                </div>
              </div>

              {/* Features */}
              <div className="space-y-2 md:space-y-3 py-2 md:py-4">
                <h3 className="text-xs md:text-sm font-semibold text-muted-foreground uppercase tracking-wide">{t('credits_success.what_you_get')}</h3>
                <div className="space-y-2 md:space-y-3">
                  <div className="flex items-center justify-between p-2 md:p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-2 md:gap-3">
                      <div className="flex items-center gap-1 text-primary">
                        <Camera className="w-3.5 h-3.5 md:w-4 md:h-4" />
                        <Type className="w-3.5 h-3.5 md:w-4 md:h-4" />
                      </div>
                      <span className="text-xs md:text-sm font-medium">{t('credits_success.ai_analysis_with_image')}</span>
                    </div>
                    <span className="text-sm md:text-base font-bold text-primary">{t('credits_success.one_credit')}</span>
                  </div>
                  <div className="flex items-center justify-between p-2 md:p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center gap-2 md:gap-3">
                      <Type className="w-3.5 h-3.5 md:w-4 md:h-4 text-primary" />
                      <span className="text-xs md:text-sm font-medium">{t('credits_success.ai_analysis_text_only')}</span>
                    </div>
                    <span className="text-sm md:text-base font-bold text-primary">{t('credits_success.zero_three_credits')}</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2 md:space-y-3 pt-1 md:pt-2">
                <Button 
                  onClick={handleBuyCredits} 
                  disabled={verifying} 
                  className="w-full shadow-md hover:shadow-lg transition-shadow text-sm md:text-base" 
                  size="lg"
                >
                  {verifying ? (
                    <>
                      <MiniGridLoader className="w-4 h-4 mr-2" />
                      {t('credits_success.processing')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-4 h-4 mr-2" />
                      {t('credits_success.buy_x_credits', { count: creditsConfig.purchase.credits })}
                    </>
                  )}
                </Button>
                
                <Button 
                  onClick={() => navigate("/track")} 
                  variant="ghost" 
                  className="w-full text-sm md:text-base"
                  disabled={verifying}
                >
                  {t('credits_success.maybe_later')}
                </Button>
              </div>

              {/* Footer Note */}
              <div className="flex items-center justify-center gap-3 md:gap-4 pt-2 md:pt-4 text-[10px] md:text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <span className="text-green-600">✓</span>
                  <span>{t('credits_success.secure_payment')}</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-green-600">✓</span>
                  <span>{t('credits_success.never_expires')}</span>
                </div>
              </div>
            </div>
          </Card>
        </div>


      </motion.div>
    );
  }

  // Success screen after payment
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="text-center max-w-md">
        <div className="mb-6">
          <div className="text-6xl mb-4">🎉</div>
          <h1 className="text-3xl font-bold mb-2">{t('credits_success.congratulations')}</h1>
          <p className="text-lg text-muted-foreground mb-6">
            {t('credits_success.purchase_successful')}
          </p>
        </div>
        {isNativeApp() ? (
          <>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-6 mb-4">
              <p className="text-lg font-semibold mb-2">{t('credits_success.next_step')}</p>
              <p className="text-muted-foreground">
                {t('credits_success.restart_app_instruction')}
              </p>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('credits_success.credits_available_after_restart')}
            </p>
          </>
        ) : (
          <>
            <div className="bg-primary/10 border border-primary/20 rounded-lg p-6 mb-4">
              <p className="text-lg font-semibold mb-2">{t('credits_success.success')}</p>
              <p className="text-muted-foreground mb-3">
                {t('credits_success.credits_added_to_account')}
              </p>
              <p className="text-sm text-muted-foreground">
                {t('credits_success.redirecting', { count: countdown })}
              </p>
            </div>
            <Button onClick={() => navigate("/track", { replace: true })} className="w-full">
              {t('credits_success.go_to_track_now')}
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default CreditsSuccess;
